import { PlanStatus, PlanSummary } from './execution-plan';

// Hand mirrors of Common's Demeter.Common.Models.Ledger (Core.Trader#265). Enums are string-wire
// (UPPER_SNAKE) with UNKNOWN as the zero member; an unknown string from a newer server is
// tolerated and read as "not known" — never switch on these exhaustively.
export type StrategyLedgerMode = 'UNKNOWN' | 'SHADOW' | 'ENFORCE';
export type StrategyLedgerReconcileState =
	| 'UNKNOWN'
	| 'RECONCILED'
	| 'SURPLUS'
	| 'SHORTFALL'
	| 'IN_FLIGHT'
	| 'ORPHANED'
	| 'UNOWNED';
export type StrategyLedgerEntryKind =
	| 'UNKNOWN'
	| 'OPENING'
	| 'FILL'
	| 'ATTRIBUTION'
	| 'AUTO'
	| 'ADJUSTMENT'
	| 'REVERSAL';
export type OrderOwnershipKind = 'UNKNOWN' | 'ATTRIBUTED' | 'AUTO';

export const STRATEGY_LEDGER_MODE_KEY = 'StrategyLedgerMode';

export interface StrategyLedgerTraderIdentity {
	fundId: string;
	// The BrokerId enum's wire string, e.g. "EZE".
	brokerId: string;
	brokerAccountId: string;
	strategyId: string;
}

export interface StrategyLedgerShare {
	identity: StrategyLedgerTraderIdentity;
	symbol: string;
	qty: number;
	// Server-computed; null for combo-leg shares. Never recomputed client-side.
	avgCost?: number | null;
}

export interface StrategyLedgerIdentityStatus {
	identity: StrategyLedgerTraderIdentity;
	bootstrapped: boolean;
	// true = no TraderConfig row with this exact identity; null = unknown (config read failed).
	orphaned: boolean | null;
}

export interface StrategyLedgerOpenOrder {
	orderId: string;
	side: string;
	qty: number;
	filledQty: number;
	strategyId?: string | null;
	updatedAtUtc: string;
}

export interface StrategyLedgerSymbolRow {
	brokerId: string;
	brokerAccountId: string;
	symbol: string;
	shares: StrategyLedgerShare[];
	brokerQty: number | null;
	unownedQty: number | null;
	// null = not decided (an unknown orphaned flag or a missing broker qty): never drawn as a group.
	state: StrategyLedgerReconcileState | string | null;
	gap: number | null;
	openOrders: StrategyLedgerOpenOrder[];
}

export interface StrategySharesResponse {
	mode: StrategyLedgerMode | string;
	identities: StrategyLedgerIdentityStatus[];
	rows: StrategyLedgerSymbolRow[];
	warnings: string[];
}

export interface StrategyLedgerEntryDTO {
	id: number;
	identity: StrategyLedgerTraderIdentity;
	symbol: string;
	kind: StrategyLedgerEntryKind | string;
	qty: number;
	price?: number | null;
	orderId?: string | null;
	legSymbol?: string | null;
	planId?: string | null;
	planLabel?: string | null;
	adoptionId?: string | null;
	cleanup: boolean;
	actorName?: string | null;
	note?: string | null;
	reversesEntryId?: number | null;
	reversedByEntryId?: number | null;
	adoptionLock?: boolean | null;
	// The entry's order source (FILL / AUTO / ATTRIBUTION): an ATTRIBUTION reads "(EXT, placed at
	// the broker)" for EXTERNAL and "(no strategy)" for DEMETER; null = not an order entry.
	orderSource?: 'DEMETER' | 'EXTERNAL' | string | null;
	// FILL: the order's quantity and its filled quantity, for "n of m posted".
	orderQty?: number | null;
	orderFilledQty?: number | null;
	// A move's adjustment pair: the shared move id and the other side's identity.
	moveId?: string | null;
	moveCounterpart?: StrategyLedgerTraderIdentity | null;
	createdAt: string;
}

export interface StrategyLedgerEntriesResponse {
	entries: StrategyLedgerEntryDTO[];
}

// A write's 200 may carry rows: [] when the post-write read-back failed: treat an empty rows after
// a write as "refresh", not as the state.
export interface StrategyLedgerWriteResult {
	entry?: StrategyLedgerEntryDTO | null;
	rows?: StrategyLedgerSymbolRow[] | null;
}

export interface StrategyLedgerAttributionRequest {
	orderId: string;
	strategyId: string;
}

export interface StrategyLedgerAdjustmentRequest {
	identity: StrategyLedgerTraderIdentity;
	symbol: string;
	// Signed: + adds to a long share.
	qty: number;
	price: number;
	note: string;
	// Optional (F52.5): a resend with the same key and body returns the recorded entry.
	requestKey?: string | null;
}

export interface StrategyLedgerMoveRow {
	from: StrategyLedgerTraderIdentity;
	symbol: string;
	qty: number;
	price: number;
}

export interface StrategyLedgerMoveRequest {
	to: StrategyLedgerTraderIdentity;
	// [] is "Start at 0".
	rows: StrategyLedgerMoveRow[];
	note: string;
	// Optional (F52.5), one per move; Start at 0 sends none.
	requestKey?: string | null;
}

export interface StrategyLedgerReversalRequest {
	entryId: number;
	note?: string | null;
}

export interface PlanAdoptAdjustment {
	symbol: string;
	qty: number;
	price: number;
	note: string;
}

// Exactly one of orderId / adjustment.
export interface PlanAdoptRequest {
	adoptionId: string;
	orderId?: string;
	adjustment?: PlanAdoptAdjustment;
}

export interface PlanUnadoptRequest {
	adoptionId: string;
}

// Core.Trader's PlanAdoptionService answers adopt and unadopt with the plan's fresh summary and the
// touched ledger rows (not an entry); rows may be [] like any write.
export interface PlanAdoptionResult {
	plan?: PlanSummary | null;
	rows?: StrategyLedgerSymbolRow[] | null;
}

// FLAT, as Core.Trader's TraderCleanupRequestDTO binds it: the identity members sit beside the
// payload, not under an `identity` key.
export interface TraderCleanupRequest {
	fundId: string;
	brokerId: string;
	brokerAccountId: string;
	strategyId: string;
	symbol: string;
	// Signed; must move the share toward zero.
	qty: number;
	price: number;
	note: string;
	// The UI-minted idempotency key (reused on every retry of one confirm).
	adoptionId: string;
}

// A cleanup is final (R14): no Reverse is offered. `reversible` is always false, so Core.Trader's
// default-omitting serializer drops it from the wire; normalizeTraderCleanupResult reads it as false.
export interface TraderCleanupResult extends StrategyLedgerWriteResult {
	reversible?: boolean;
	note?: string;
}

// The 409 body every ledger refusal carries.
export interface LedgerRefusalBody {
	code: string;
	message?: string;
	// CO_TENANT_REFUSED (Core.Config) only.
	coTenantStrategyId?: string;
	reason?: 'MODE' | 'IGNORE_MISC' | string;
}

export interface SystemSettingConfig {
	key: string;
	value: string;
	updatedAt?: string;
	updatedBy?: string | null;
}

export type LedgerPlanStatus = PlanStatus | string;

// Core.Trader serializes its ledger verb answers with WhenWritingDefault, so every null/0/false/
// UNKNOWN member is OMITTED (Core.API writes the same Common types with explicit nulls). These
// helpers put the Core.Trader answers back on the one shape the interfaces above describe.
export function normalizeLedgerShare(share: StrategyLedgerShare): StrategyLedgerShare {
	return { ...share, qty: share.qty ?? 0, avgCost: share.avgCost ?? null };
}

export function normalizeLedgerOpenOrder(order: StrategyLedgerOpenOrder): StrategyLedgerOpenOrder {
	return { ...order, qty: order.qty ?? 0, filledQty: order.filledQty ?? 0 };
}

export function normalizeLedgerRow(row: StrategyLedgerSymbolRow): StrategyLedgerSymbolRow {
	return {
		...row,
		shares: (row.shares ?? []).map(normalizeLedgerShare),
		brokerQty: row.brokerQty ?? null,
		unownedQty: row.unownedQty ?? null,
		state: row.state ?? null,
		gap: row.gap ?? null,
		openOrders: (row.openOrders ?? []).map(normalizeLedgerOpenOrder)
	};
}

export function normalizeLedgerEntry(entry: StrategyLedgerEntryDTO): StrategyLedgerEntryDTO {
	return {
		...entry,
		kind: entry.kind ?? 'UNKNOWN',
		qty: entry.qty ?? 0,
		cleanup: entry.cleanup ?? false,
		price: entry.price ?? null,
		orderId: entry.orderId ?? null,
		legSymbol: entry.legSymbol ?? null,
		planId: entry.planId ?? null,
		planLabel: entry.planLabel ?? null,
		adoptionId: entry.adoptionId ?? null,
		actorName: entry.actorName ?? null,
		note: entry.note ?? null,
		reversesEntryId: entry.reversesEntryId ?? null,
		reversedByEntryId: entry.reversedByEntryId ?? null,
		adoptionLock: entry.adoptionLock ?? null,
		orderSource: entry.orderSource ?? null,
		orderQty: entry.orderQty ?? null,
		orderFilledQty: entry.orderFilledQty ?? null,
		moveId: entry.moveId ?? null,
		moveCounterpart: entry.moveCounterpart ?? null
	};
}

export function normalizePlanAdoptionResult(result: PlanAdoptionResult): PlanAdoptionResult {
	return {
		...result,
		plan: result.plan ?? null,
		rows: (result.rows ?? []).map(normalizeLedgerRow)
	};
}

export function normalizeTraderCleanupResult(result: TraderCleanupResult): TraderCleanupResult {
	return {
		...result,
		entry: result.entry ? normalizeLedgerEntry(result.entry) : null,
		rows: (result.rows ?? []).map(normalizeLedgerRow),
		reversible: result.reversible ?? false
	};
}
